import { AxiosResponse } from 'axios'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { $axios } from './axios'
import { EmailType } from '../../server/models/Email'
import { InboxNewsArticleType } from '../../server/models/InboxNewsArticle'

// Unified source type - can be either Email or InboxNewsArticle
export type SourceType = EmailType | InboxNewsArticleType

// Type guard to check if source is an Email
export const isEmail = (source: SourceType): source is EmailType => {
  return 'subject' in source
}

// Type guard to check if source is an InboxNewsArticle
export const isArticle = (source: SourceType): source is InboxNewsArticleType => {
  return 'inboxId' in source && 'publishDate' in source
}

// Normalize date to midnight for consistent caching
const normalizeToMidnight = (date: Date): Date => {
  const normalized = new Date(date)
  normalized.setHours(0, 0, 0, 0)
  return normalized
}

const getSources = (
  inboxId: string,
  params: {
        startDate?: Date
        endDate?: Date
        ignore?: boolean
    } = {}
): Promise<AxiosResponse<SourceType[]>> => {
  const { startDate, endDate } = params

  return $axios.get(`/inboxes/${inboxId}/sources`, {
    params: {
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      ignore: params.ignore
    }
  })
}

export type UseSourcesParams = {
    startDate?: Date
    endDate?: Date
    ignore?: boolean
}

export const useSourcesQuery = (
  inboxId: string,
  params: UseSourcesParams = {},
  options?: Omit<UseQueryOptions<SourceType[], Error>, 'queryKey' | 'queryFn'>
) => {
  // Normalize dates to midnight for consistent cache keys
  const normalizedStartDate = params.startDate ? normalizeToMidnight(params.startDate) : undefined
  const normalizedEndDate = params.endDate ? normalizeToMidnight(params.endDate) : undefined

  return useQuery<SourceType[], Error>({
    queryKey: ['sources', inboxId, normalizedStartDate?.toISOString(), normalizedEndDate?.toISOString(), params.ignore],
    queryFn: async () => {
      const res = await getSources(inboxId, {
        ...params,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate
      })
      return res.data
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - liberal caching
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for a long time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options
  })
}

export const getSourcesCount = (
  inboxId: string,
  params: {
        startDate?: Date
        endDate?: Date
        ignore?: boolean
    } = {}
): Promise<AxiosResponse<{ count: number }>> => {
  const { startDate, endDate } = params

  return $axios.get(`/inboxes/${inboxId}/sources/count`, {
    params: {
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      ignore: params.ignore
    }
  })
}

export type UseSourcesCountParams = {
    startDate?: Date
    endDate?: Date
    ignore?: boolean
}

export const useSourcesCountQuery = (
  inboxId: string,
  params: UseSourcesCountParams = {},
  options?: Omit<UseQueryOptions<number, Error>, 'queryKey' | 'queryFn'>
) => {
  // Normalize dates to midnight for consistent cache keys
  const normalizedStartDate = params.startDate ? normalizeToMidnight(params.startDate) : undefined
  const normalizedEndDate = params.endDate ? normalizeToMidnight(params.endDate) : undefined

  return useQuery<number, Error>({
    queryKey: ['sourcesCount', inboxId, normalizedStartDate?.toISOString(), normalizedEndDate?.toISOString(), params.ignore],
    queryFn: async () => {
      const res = await getSourcesCount(inboxId, {
        ...params,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate
      })
      return res.data.count
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - liberal caching
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for a long time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options
  })
}
